import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { AbstractCRUD, CRUDService, ExceptionService, PaginatedList, UtilsService } from '../../xform-compat';
import {
  DistributionList,
  DistributionListMember,
  InputDistributionListMember,
  InputDistributionListMembers
} from '../../shared/models/distribution-list.model';

/**
 * Data Service for DistributionList.
 *
 * There is no create or delete: the 22 lists are code-defined and seeded. Only
 * their per-environment membership is editable.
 */
@Injectable({
  providedIn: 'root'
})
export class DistributionListService extends AbstractCRUD<DistributionList> implements CRUDService<DistributionList> {

  /**
   * All 22 lists fit comfortably on one page. The backend defaults pagesize to
   * 25, which happens to be enough today - but a 23rd list would silently
   * truncate the table, so the size is requested explicitly.
   */
  private static readonly PAGE_SIZE = 100;

  constructor(
    exceptionService: ExceptionService,
    http: HttpClient,
    utilsService: UtilsService
  ) {
    super(exceptionService, http, utilsService);
  }

  public getResourcePath(): string {
    return '/rms/distribution-lists';
  }

  /**
   * Lists every distribution list with its member count for one environment.
   *
   * Delegates to AbstractCRUD.getRecords, which builds the query string through
   * utilsService.generateParams and already pipes catchBadResponse.
   */
  public getAllForEnvironment(environment: string): Observable<PaginatedList<DistributionList>> {
    return super.getRecords({page: 0, pagesize: DistributionListService.PAGE_SIZE, environment});
  }

  /**
   * Retrieves the members of one list in one environment.
   */
  public getMembers(id: number, environment: string): Observable<Array<DistributionListMember>> {
    return super.getHTTP()
      .get<Array<DistributionListMember>>(`${this.getApiPath()}/${id}/members`,
        {params: this.utilsService.generateParams({environment})})
      .pipe(catchError(ex => this.businessErrorOnly(ex)));
  }

  /**
   * Replaces the complete member set of one list in one environment. The body
   * is the full membership, not a delta: members absent from it are removed.
   */
  public saveMembers(id: number, environment: string,
                     members: Array<InputDistributionListMember>): Observable<Array<DistributionListMember>> {
    const body: InputDistributionListMembers = { members };

    return super.getHTTP()
      .put<Array<DistributionListMember>>(`${this.getApiPath()}/${id}/members`, body,
        {params: this.utilsService.generateParams({environment})})
      .pipe(catchError(ex => this.businessErrorOnly(ex)));
  }

  /**
   * Narrows what a caller is allowed to put in front of an admin.
   *
   * Only a business rejection carries a message fit to show. On any other failure
   * ExceptionService rethrows HttpErrorResponse.message verbatim - "Http failure response for
   * /rms/distribution-lists/42/members?environment=prod: 500 Internal Server Error" - which would
   * put the endpoint, the list id, the environment and the upstream status in the banner and in
   * any screenshot of it. Those come back as a falsy error so the caller falls back to its own
   * generic message, while a mapped BUS_ code still reaches the admin intact.
   */
  private businessErrorOnly(ex: any): Observable<any> {
    const code = ex?.error?.code;

    return super.getException().catchBadResponse(ex)
      .pipe(catchError(message => throwError(this.isShowable(code, message) ? message : null)));
  }

  /** A BUS_ code that en.json actually maps; an unmapped one resolves to the literal key. */
  private isShowable(code: string, message: any): boolean {
    return !!code && typeof message === 'string' && !message.startsWith('API_ERRORS.');
  }
}
